/**
 * Repayment-risk scoring. Pure — no network, no globals, no DOM.
 *
 * MODEL
 * -----
 * A regularised logistic regression over five keys — industry sector, loan
 * size, term, SBA business-age category, jobs per dollar — fitted on the
 * compacted SBA 7(a) counts by scripts/build-model.ts, with older loans
 * down-weighted. The target is charge-off within a fixed horizon after
 * approval, which can be measured on recent loans. The horizon and the
 * weighting were chosen by out-of-time back-test; see docs/BACKTEST.md.
 *
 * Coefficients are centred on the typical level of each factor, so every
 * factor's contribution is one additive number and the "why" panel reads the
 * model directly rather than being decorated separately.
 *
 * WHAT IT IS NOT
 * --------------
 * The SBA data contains only approved loans. p estimates how often loans like
 * this one charged off AFTER a lender approved them. It is not an approval
 * probability and not a credit decision, and it knows nothing about credit
 * history, cash flow or collateral — the things lenders actually underwrite.
 */

import type {
  Band, BusinessAgeKey, Confidence, Factor, JobsBucket, RiskLevel, RiskTable,
  ScoreInput, ScoreResult, SizeBucket, SpreadDist, TermBucket, UseOfFunds,
} from './types.js';
import { naics2, sectorLabel } from './naics.js';
import {
  PROGRAM_MAX_LOAN, monthlyPayment, round2, spreadTierFor, totalInterest,
} from './rates.js';

/** A joint cell below this many resolved loans is not trusted on its own. */
const MIN_CELL_N = 50;

/** Clamp probabilities away from 0 and 1 so logit stays finite. */
const EPS = 1e-6;

export function logit(p: number): number {
  const q = Math.min(1 - EPS, Math.max(EPS, p));
  return Math.log(q / (1 - q));
}

export function invLogit(x: number): number {
  return 1 / (1 + Math.exp(-x));
}

export function sizeBucketFor(amount: number): SizeBucket {
  if (amount <= 50_000) return 'le50k';
  if (amount <= 250_000) return 'le250k';
  if (amount <= 350_000) return 'le350k';
  if (amount <= 1_000_000) return 'le1m';
  return 'gt1m';
}

export function termBucketFor(termMonths: number): TermBucket {
  if (termMonths <= 84) return 'le84';
  if (termMonths <= 120) return 'le120';
  if (termMonths <= 240) return 'le240';
  return 'gt240';
}

/**
 * Map an applicant to SBA's current BusinessAge coding (see BusinessAgeKey).
 * Buying an existing business is its own SBA category regardless of how old
 * the business is.
 */
export function businessAgeFor(yearsInOperation: number, isChangeOfOwnership = false): BusinessAgeKey {
  if (isChangeOfOwnership) return 'change-of-ownership';
  if (yearsInOperation <= 0) return 'startup';
  if (yearsInOperation <= 2) return 'new-le2';
  return 'existing-gt2';
}

export const BUSINESS_AGE_LABELS: Record<BusinessAgeKey, string> = {
  startup: 'Startup — loan funds will open the business',
  'new-le2': 'New business, 2 years or less',
  'existing-gt2': 'Existing business, more than 2 years',
  'change-of-ownership': 'Change of ownership',
  unknown: 'Not stated',
};

/** Jobs supported per $100k requested. */
export function jobsBucketFor(jobs: number | undefined, amount: number): JobsBucket {
  if (jobs === undefined || jobs <= 0 || amount <= 0) return 'none';
  const per100k = jobs / (amount / 100_000);
  if (per100k < 1) return 'low';
  if (per100k < 3) return 'medium';
  return 'high';
}

export const SIZE_LABELS: Record<SizeBucket, string> = {
  le50k: '$50,000 or less',
  le250k: '$50,001-$250,000',
  le350k: '$250,001-$350,000',
  le1m: '$350,001-$1,000,000',
  gt1m: 'over $1,000,000',
};

export const TERM_LABELS: Record<TermBucket, string> = {
  le84: '7 years or less',
  le120: '7-10 years',
  le240: '10-20 years',
  gt240: 'over 20 years',
};

const JOBS_LABELS: Record<JobsBucket, string> = {
  none: 'not provided',
  low: 'under 1 job per $100k',
  medium: '1-3 jobs per $100k',
  high: '3+ jobs per $100k',
};

export const USE_OF_FUNDS_LABELS: Record<UseOfFunds, string> = {
  'working-capital': 'Working capital',
  equipment: 'Equipment purchase',
  'real-estate': 'Real estate acquisition',
  'business-acquisition': 'Business acquisition',
  'debt-refinancing': 'Debt refinancing',
};

function verdictFor(delta: number): { direction: Factor['direction']; verdict: string } {
  if (delta <= -0.12) return { direction: 'favorable', verdict: 'Favorable' };
  if (delta >= 0.12) return { direction: 'unfavorable', verdict: 'Below typical' };
  return { direction: 'neutral', verdict: 'About typical' };
}

function horizonText(months: number): string {
  return months % 12 === 0 ? `${months / 12} years` : `${months} months`;
}

function comparisonDetail(level: RiskLevel | undefined, overall: number, horizonMonths: number): string {
  if (!level || level.n <= 0) return 'No comparable loans in the SBA data.';
  const pct = (level.rate * 100).toFixed(1);
  const basePct = (overall * 100).toFixed(1);
  const n = level.n.toLocaleString('en-US');
  const within = `within ${horizonText(horizonMonths)} of approval`;
  if (level.rate > overall) {
    return `${pct}% of comparable SBA loans were charged off ${within}, against ${basePct}% overall (${n} loans).`;
  }
  if (level.rate < overall) {
    return `${pct}% of comparable SBA loans were charged off ${within}, better than ${basePct}% overall (${n} loans).`;
  }
  return `${pct}% were charged off ${within}, in line with the overall rate (${n} loans).`;
}

/** Keys the model scores on. Everything about an applicant reduces to these. */
export interface RiskKeys {
  naics2: string | null;
  size: SizeBucket;
  term: TermBucket;
  age: BusinessAgeKey;
  jobs: JobsBucket;
}

export interface RiskBreakdown {
  /** Estimated horizon charge-off probability. */
  p: number;
  /** p relative to the overall rate in the table. */
  ratio: number;
  adj: Record<'naics' | 'size' | 'term' | 'age' | 'jobs' | 'sizeTerm', { delta: number; n: number }>;
}

/**
 * The model itself: a logistic regression over the five keys, fitted jointly
 * on the compacted SBA counts (scripts/build-model.ts). Fitting jointly matters:
 * loan size and term are strongly linked (large loans are usually long
 * real-estate loans), and adding their separate effects would count the same
 * risk twice. Each coefficient is centred on the typical level, so each
 * factor's contribution is still one additive, explainable number.
 *
 * Both the app (via score()) and the back-test call this function, so the
 * numbers that were validated are the numbers users see.
 */
export function riskFromKeys(keys: RiskKeys, table: RiskTable): RiskBreakdown {
  const { intercept, coef } = table.logistic;
  const lvl = (level: RiskLevel | undefined) => level?.n ?? 0;
  // A level the model did not see (unrecognised industry, jobs not provided)
  // contributes zero: it is treated as typical rather than quietly penalised.
  const adj = {
    naics: { delta: keys.naics2 ? (coef.naics[keys.naics2] ?? 0) : 0, n: lvl(keys.naics2 ? table.byNaics[keys.naics2] : undefined) },
    size: { delta: coef.size[keys.size] ?? 0, n: lvl(table.bySize[keys.size]) },
    term: { delta: coef.term[keys.term] ?? 0, n: lvl(table.byTerm[keys.term]) },
    age: { delta: coef.age[keys.age] ?? 0, n: lvl(table.byBusinessAge[keys.age]) },
    jobs: keys.jobs === 'none' ? { delta: 0, n: 0 } : { delta: coef.jobs[keys.jobs] ?? 0, n: lvl(table.byJobs[keys.jobs]) },
    sizeTerm: { delta: coef.sizeTerm?.[`${keys.size}|${keys.term}`] ?? 0, n: 0 },
  };
  const total = intercept + adj.naics.delta + adj.size.delta + adj.term.delta + adj.age.delta
    + adj.jobs.delta + adj.sizeTerm.delta;
  const p = invLogit(total);
  const overall = table.global.rate;
  return { p, ratio: overall > 0 ? p / overall : 1, adj };
}

/**
 * Band cut-offs on p / overall rate, set from the back-test deciles (see
 * docs/BACKTEST.md): the safest half of unseen loans almost never charged off,
 * deciles 6-9 sit on a plateau the public fields cannot separate further, and
 * the top decile is clearly riskier. The bands follow that structure rather
 * than implying finer distinctions than the data supports.
 */
export const BAND_CUTOFFS = { lower: 0.75, higher: 2.25 } as const;

export function bandFor(ratio: number): { band: Band; bandLabel: string } {
  if (ratio <= BAND_CUTOFFS.lower) return { band: 'lower-risk', bandLabel: 'Lower risk than typical' };
  if (ratio <= BAND_CUTOFFS.higher) return { band: 'typical-risk', bandLabel: 'About typical risk' };
  return { band: 'higher-risk', bandLabel: 'Higher risk than typical' };
}

function confidenceFor(input: ScoreInput, cellN: number, usedJointCell: boolean): Confidence {
  const reasons: string[] = [];
  let score = 0;

  if (usedJointCell && cellN >= 500) {
    score += 2;
    reasons.push(`Matched ${cellN.toLocaleString('en-US')} closely comparable SBA loans.`);
  } else if (usedJointCell) {
    score += 1;
    reasons.push(`Matched ${cellN.toLocaleString('en-US')} comparable SBA loans — a thin sample, so the range is wider.`);
  } else {
    reasons.push('No close match in the SBA extract, so the estimate combines each factor separately. The range is wider as a result.');
  }

  if (input.annualRevenue !== undefined && input.annualRevenue > 0) {
    score += 1;
    reasons.push('Revenue supplied, so debt-service coverage could be computed.');
  } else {
    reasons.push('Revenue not supplied — add it to compute debt-service coverage and narrow this.');
  }

  if (input.jobsSupported !== undefined && input.jobsSupported > 0) score += 1;

  const level = Math.max(1, Math.min(4, score + 1)) as 1 | 2 | 3 | 4;
  const label = level >= 4 ? 'High confidence'
    : level === 3 ? 'Moderate confidence'
      : level === 2 ? 'Limited confidence'
        : 'Low confidence';
  return { level, label, reasons };
}

/** Most specific observed-spread group with enough loans, falling back outward. */
export function spreadDistFor(
  table: RiskTable, sizeB: SizeBucket, termB: TermBucket,
): { dist: SpreadDist; provenance: 'size-term' | 'size' | 'overall' } {
  const st = table.rateSpreads.bySizeTerm[`${sizeB}|${termB}`];
  if (st && st.n >= MIN_CELL_N) return { dist: st, provenance: 'size-term' };
  const s = table.rateSpreads.bySize[sizeB];
  if (s && s.n >= MIN_CELL_N) return { dist: s, provenance: 'size' };
  return { dist: table.rateSpreads.global, provenance: 'overall' };
}

export interface ScoreOptions {
  basePct: number;
  baseAsOf: string;
  baseIsFallback: boolean;
}

export function score(input: ScoreInput, table: RiskTable, opts: ScoreOptions): ScoreResult {
  const notes: string[] = [];
  const overall = table.global.rate;
  const horizon = table.meta.outcome.horizonMonths;

  const two = naics2(input.naics);
  if (!two) {
    notes.push('Industry code not recognised, so no industry adjustment was applied.');
  }

  const sizeB = sizeBucketFor(input.amount);
  const termB = termBucketFor(input.termMonths);
  const ageKey = businessAgeFor(input.yearsInOperation, input.isChangeOfOwnership);
  const jobsB = jobsBucketFor(input.jobsSupported, input.amount);

  const cellKey = two ? `${two}|${sizeB}|${termB}` : '';
  const cell = cellKey ? table.cells[cellKey] : undefined;
  const usedJointCell = !!cell && cell.n >= MIN_CELL_N;

  // Each factor's contribution below is the exact term riskFromKeys summed —
  // the display reads the model; the model is not fitted to the display.
  const { p, ratio, adj } = riskFromKeys(
    { naics2: two, size: sizeB, term: termB, age: ageKey, jobs: jobsB }, table,
  );
  const naicsAdj = adj.naics;
  const sizeAdj = adj.size;
  const termAdj = adj.term;
  const ageAdj = adj.age;
  const jobsAdj = adj.jobs;

  const rawFactors: Array<Omit<Factor, 'weight'>> = [
    {
      key: 'industry',
      label: 'Industry',
      value: two ? sectorLabel(input.naics) : 'Not recognised',
      contribution: naicsAdj.delta,
      n: naicsAdj.n,
      detail: comparisonDetail(two ? table.byNaics[two] : undefined, overall, horizon),
      ...verdictFor(naicsAdj.delta),
    },
    {
      key: 'amount',
      label: 'Requested amount',
      value: `$${Math.round(input.amount).toLocaleString('en-US')} (${SIZE_LABELS[sizeB]})`,
      contribution: sizeAdj.delta,
      n: sizeAdj.n,
      detail: comparisonDetail(table.bySize[sizeB], overall, horizon),
      ...verdictFor(sizeAdj.delta),
    },
    {
      key: 'term',
      label: 'Term requested',
      value: `${input.termMonths} months (${TERM_LABELS[termB]})`,
      contribution: termAdj.delta,
      n: termAdj.n,
      detail: comparisonDetail(table.byTerm[termB], overall, horizon),
      ...verdictFor(termAdj.delta),
    },
    {
      key: 'operating-history',
      label: 'Operating history',
      value: BUSINESS_AGE_LABELS[ageKey],
      contribution: ageAdj.delta,
      n: ageAdj.n,
      detail: ageKey === 'change-of-ownership' && !(table.byBusinessAge[ageKey]?.n)
        ? 'SBA only began recording change of ownership as its own category in FY2018, so none of those loans have finished repaying yet. This factor does not move the estimate.'
        : `${comparisonDetail(table.byBusinessAge[ageKey], overall, horizon)} Matched to SBA's own business-age category, as lenders record it today.`,
      ...verdictFor(ageAdj.delta),
    },
  ];

  if (jobsB !== 'none') {
    rawFactors.push({
      key: 'jobs',
      label: 'Jobs per dollar requested',
      value: `${input.jobsSupported} jobs (${JOBS_LABELS[jobsB]})`,
      contribution: jobsAdj.delta,
      n: jobsAdj.n,
      detail: comparisonDetail(table.byJobs[jobsB], overall, horizon),
      ...verdictFor(jobsAdj.delta),
    });
  }

  // The model includes a size x term interaction. It is shown as its own row so
  // the displayed contributions add up to exactly what the model computed.
  if (adj.sizeTerm.delta !== 0) {
    rawFactors.push({
      key: 'size-term',
      label: 'Amount and term together',
      value: `${SIZE_LABELS[sizeB]} over ${TERM_LABELS[termB]}`,
      contribution: adj.sizeTerm.delta,
      n: table.cells[cellKey]?.n ?? 0,
      detail: 'Some combinations of loan amount and term performed differently from what the amount and the term suggest on their own. This adjusts for that.',
      ...verdictFor(adj.sizeTerm.delta),
    });
  }

  // Use of funds is collected and displayed, but the 7(a) FOIA extract has no
  // use-of-funds field — so it carries no weight, and the panel says so.
  // Showing a bar for it would imply an influence it does not have.
  rawFactors.push({
    key: 'use-of-funds',
    label: 'Use of funds',
    value: USE_OF_FUNDS_LABELS[input.useOfFunds],
    contribution: 0,
    n: 0,
    detail: 'Not weighted: the public SBA 7(a) dataset records no use-of-funds field, so there is nothing to measure it against. It shapes the projection, not the estimate.',
    direction: 'neutral',
    verdict: 'Not weighted',
  });

  const maxAbs = Math.max(...rawFactors.map((f) => Math.abs(f.contribution)), EPS);
  const factors: Factor[] = rawFactors.map((f) => ({
    ...f,
    weight: Math.abs(f.contribution) / maxAbs,
  }));

  // Rate: what lenders actually charged comparable SBA loans. Each observed
  // spread is InitialInterestRate minus the prime rate on that loan's approval
  // date (see the build script), so adding today's prime back gives a range in
  // today's terms. The Appendix 18 ceiling is a hard cap, never a target.
  const tier = spreadTierFor(input.amount);
  const ceilingPct = round2(opts.basePct + tier.spreadPct);
  const { dist, provenance } = spreadDistFor(table, sizeB, termB);
  const cap = (spread: number) => round2(Math.min(ceilingPct, Math.max(opts.basePct, opts.basePct + spread)));
  const low = cap(dist.p10);
  const likely = cap(dist.p50);
  const high = cap(dist.p90);
  const clippedByCeiling = opts.basePct + dist.p90 > ceilingPct;

  const conf = confidenceFor(input, cell?.n ?? 0, usedJointCell);
  const principal = Math.max(0, input.amount);

  if (provenance !== 'size-term') {
    notes.push(provenance === 'size'
      ? 'Too few recent SBA loans matched both your amount and term, so the rate range uses all recent loans of a similar amount.'
      : 'Too few recent SBA loans of a similar amount, so the rate range uses all recent variable-rate SBA loans.');
  }
  if (clippedByCeiling) {
    notes.push(`Some comparable loans priced above today's SBA maximum for this size; the range is capped at ${ceilingPct.toFixed(2)}%.`);
  }

  if (!usedJointCell && two) {
    notes.push('No single group of closely comparable SBA loans was large enough to rely on, so each factor was applied separately.');
  }
  if (opts.baseIsFallback) {
    notes.push(`The prime rate could not be refreshed, so the pinned value from ${opts.baseAsOf} was used. The rate range may be out of date.`);
  }

  const banded = bandFor(ratio);

  const result: ScoreResult = {
    band: banded.band,
    bandLabel: banded.bandLabel,
    estimatedChargeOffRate: p,
    relativeToOverall: ratio,
    rate: {
      basePct: opts.basePct,
      baseAsOf: opts.baseAsOf,
      baseIsFallback: opts.baseIsFallback,
      maxSpreadPct: tier.spreadPct,
      tierLabel: tier.label,
      ceilingPct,
      observedSpread: dist,
      spreadProvenance: provenance,
      estimateLowPct: low,
      estimateLikelyPct: likely,
      estimateHighPct: high,
      clippedByCeiling,
    },
    payment: {
      lowMonthly: Math.round(monthlyPayment(principal, low, input.termMonths)),
      likelyMonthly: Math.round(monthlyPayment(principal, likely, input.termMonths)),
      highMonthly: Math.round(monthlyPayment(principal, high, input.termMonths)),
      totalInterestLikely: Math.round(totalInterest(principal, likely, input.termMonths)),
    },
    factors,
    confidence: conf,
    basis: {
      cellKey,
      cellN: cell?.n ?? 0,
      cellProvenance: usedJointCell ? 'joint-cell' : 'marginals',
      overallRate: overall,
      asOf: table.meta.asOf,
    },
    notes,
  };

  // An out-of-program request gets an honest refusal, not an extrapolation.
  if (input.amount > PROGRAM_MAX_LOAN) {
    return {
      ...result,
      band: 'out-of-program',
      bandLabel: 'Outside the 7(a) program',
      notes: [
        `The standard 7(a) maximum is $${PROGRAM_MAX_LOAN.toLocaleString('en-US')}, and this request is above it, so the estimate below does not apply.`,
        ...result.notes,
      ],
    };
  }

  return result;
}
